import dayjs, { type Dayjs } from 'dayjs';
import { ARRETES } from '@/constants';
import type { ArreteConfigType } from '@/types';

/**
 * Indique si la date est dans la fenêtre de création de l'arrêté annuel.
 *
 * Sans config : fenêtre légale du 15 au 31 décembre (mois 11 = décembre en notation dayjs 0-indexée).
 * Avec config (dates fournies par le back, cf. RegieResource.arreteConfig) : fenêtre
 * [windowOpenDate, exerciceEndDate] inclusive, comparée directement aux dates complètes — cohérent
 * avec le back qui résout déjà l'année cible. Config absente/invalide => repli sur la règle légale.
 */
export const estDansFenetreArreteAnnuel = (date: Dayjs, config?: ArreteConfigType): boolean => {
  if (config) {
    const ouverture = dayjs(config.windowOpenDate);
    const fin = dayjs(config.exerciceEndDate);
    if (ouverture.isValid() && fin.isValid()) {
      return !date.isBefore(ouverture, 'day') && !date.isAfter(fin, 'day');
    }
  }

  return date.month() === 11 && date.date() >= 15;
};

/**
 * Détermine l'année à clôturer par un arrêté annuel à partir de l'état du dernier arrêté de la régie.
 * Règle métier : le dernier arrêté doit être un Périodique dont la date de fin atteint au moins le
 * seuil d'ouverture de SON année. L'année cible est l'année de cette date de fin (pas l'année
 * courante : un périodique finissant après le seuil reste créable même consulté plus tard).
 *
 * Le seuil est le 15 décembre par défaut, ou le jour/mois d'ouverture configuré (cf. arreteConfig)
 * rebasé sur l'année du périodique (la règle reste relative à cette année).
 *
 * @returns l'année à clôturer si la régie remplit la condition, sinon `null`.
 */
export const getAnneeArreteAnnuelCreable = (
  typeDernierArrete?: string,
  dateFinDernierArrete?: string,
  config?: ArreteConfigType,
): number | null => {
  if (typeDernierArrete !== ARRETES.TYPE_PERIODIQUE || !dateFinDernierArrete) return null;
  const fin = dayjs(dateFinDernierArrete);
  if (!fin.isValid()) return null;
  const annee = fin.year();

  let seuil = dayjs(`${annee}-12-15`);
  if (config) {
    const ouverture = dayjs(config.windowOpenDate);
    if (ouverture.isValid()) {
      // Conserve le jour/mois configuré, mais calé sur l'année du dernier périodique.
      seuil = ouverture.year(annee);
    }
  }

  if (fin.isBefore(seuil, 'day')) return null;
  return annee;
};
